import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import Validator from 'validatorjs';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';

import {
  Form, Button, Modal, Header, Icon, Message, Radio, Select, Checkbox, Table,
} from 'semantic-ui-react';

import { formatDateOnly } from '../../utilities/dateUtils';
import {
  STALE_VERSION_HEADER,
  STALE_VERSION_GUIDANCE,
  NO_MASTER_PRODUCTS_HEADER,
  NO_MASTER_PRODUCTS_GUIDANCE,
  MASTER_PRODUCTS_ERROR_HEADER,
  MASTER_PRODUCTS_ERROR_GUIDANCE,
  MASTER_PRODUCTS_ONLY_GUIDANCE,
  SELECT_ALL_GUIDANCE,
  productsForPartySide,
  NO_PRODUCTS_ON_SIDE_HEADER,
  NO_PRODUCTS_ON_SIDE_GUIDANCE,
} from '../../constants/defaultCoverageOptOut';

// Three radios, matching the Masters screen's Payer Side (v2) — but only two
// real backend branches: MASTER_SELLER and MSTR_SELLER_PAID both veto through
// the same sellerNumber column (see DefaultCoverageServiceImpl#resolveMaster),
// so "seller" and "seller_paid" here differ only in which products the picker
// offers, never in what gets submitted.
export const PARTY_SIDE = {
  BUYER: 'buyer',
  SELLER: 'seller',
  SELLER_PAID: 'seller_paid',
};

export const NO_PARTY_ERROR = 'Choose a party side first.';
export const NO_SELLER_NUMBER_ERROR = 'Enter the Seller Number.';
export const NO_BUYER_IDENTIFIER_ERROR = 'Enter a Buyer Number, an Auction Access Number, or both — at least one is required.';
export const NO_PRODUCTS_SELECTED_ERROR = 'Select at least one product to opt out of.';

// An opt-out row is keyed on exactly one side (v2): sellerNumber alone, or
// buyerNumber and/or auctionAccessNumber — never a mix, never all empty. The
// UI only ever shows the fields for the chosen side, so "a mix" cannot happen
// here; this is the "at least one populated" half of the server's rule.
// Seller and seller-paid share the sellerNumber branch, so anything that is
// not the buyer side falls through to the same check.
export const partySelectionError = (partySide, buyerNumber, sellerNumber, auctionAccessNumber) => {
  if (!partySide) return NO_PARTY_ERROR;
  if (partySide !== PARTY_SIDE.BUYER) {
    return isEmpty(String(sellerNumber || '').trim()) ? NO_SELLER_NUMBER_ERROR : null;
  }
  const hasBuyer = !isEmpty(String(buyerNumber || '').trim());
  const hasAan = !isEmpty(String(auctionAccessNumber || '').trim());
  return hasBuyer || hasAan ? null : NO_BUYER_IDENTIFIER_ERROR;
};

// A party number is a customer number: exactly 7 digits, nothing else. The APMS
// column is VARCHAR(16), so this is a business rule tighter than the schema and
// is stated here rather than inferred from the column width.
export const PARTY_NUMBER_LENGTH = 7;
const PARTY_NUMBER_PATTERN = /^\d{7}$/;

// The auction access number ("100 mil" / buyer rep number): exactly 9 digits,
// matching APMS's AAN_PATTERN.
export const AAN_LENGTH = 9;
const AAN_PATTERN = /^\d{9}$/;

// APMS stores notes as VARCHAR(500). The form caps well under that so a note can
// never be silently truncated on the way down.
export const NOTES_MAX_LENGTH = 256;

export const PARTY_NUMBER_FORMAT_ERROR = `Enter exactly ${PARTY_NUMBER_LENGTH} digits - numbers only, no spaces, dashes or letters.`;
export const AAN_FORMAT_ERROR = `Enter exactly ${AAN_LENGTH} digits - numbers only, no spaces, dashes or letters.`;
export const NOTES_TOO_LONG_ERROR = `Notes must be ${NOTES_MAX_LENGTH} characters or fewer.`;
export const END_BEFORE_START_ERROR = 'End Date must not be before Start Date.';

// Format check for the party number, kept separate from `required` so an empty
// field reports "required" and a malformed one reports the format.
//
// Deliberately a regex and NOT validatorjs's `digits:7` rule: that rule accepts
// anything `Number()` can parse and then only counts characters, so '1234.56',
// '1.2e+45' and '0x12345' all pass it at length 7. Only a regex means "seven
// digits". It also keeps free text out of a value that crosses two AWS WAFs
// (MC -> tappy -> APMS) on its way to the database.
export const partyNumberFormatError = partyNumber => {
  const value = String(partyNumber || '').trim();
  // `required` owns the empty case; reporting both at once would be noise.
  if (isEmpty(value)) return null;
  return PARTY_NUMBER_PATTERN.test(value) ? null : PARTY_NUMBER_FORMAT_ERROR;
};

// Same shape as partyNumberFormatError: absent is fine (the buyer branch allows
// the AAN to be the only identifier, or to be omitted entirely), present-and-
// malformed is not.
export const aanFormatError = auctionAccessNumber => {
  const value = String(auctionAccessNumber || '').trim();
  if (isEmpty(value)) return null;
  return AAN_PATTERN.test(value) ? null : AAN_FORMAT_ERROR;
};

// Measured on the trimmed value because that is what submit actually sends.
export const notesLengthError = notes => (
  String(notes || '').trim().length > NOTES_MAX_LENGTH ? NOTES_TOO_LONG_ERROR : null
);

// Submit gate for the create flow. With no product carrying default (master)
// coverage there is nothing an opt-out could suppress, so the create is
// refused - both on the button and inside the submit handler, which is why it
// is a predicate and not just a disabled attribute. Never blocks an edit: that
// form does not pick a product at all.
// `masterProductsError` blocks the create for a different reason than
// `hasNoMasterProducts`: not "there is nothing to suppress" but "we have no
// product list, so any product on this row would be a guess". Both block; only
// the message differs. Third arg defaults to false so existing callers and
// tests keep their meaning.
export const isCreateBlockedWithoutMasters = (
  isEdit,
  hasNoMasterProducts,
  masterProductsError = false
) => !isEdit && (Boolean(hasNoMasterProducts) || Boolean(masterProductsError));

// partyNumber/AAN are not `required` here: which of them is required depends on
// the chosen side (seller needs sellerNumber; buyer needs buyerNumber and/or
// auctionAccessNumber) — see partySelectionError, which validatorjs cannot express.
const validationRules = {
  partySide: 'required',
  productId: 'required',
};

const DefaultCoverageOptOutForm = ({
  isModalOpen,
  onCloseHandler,
  onSubmitHandler,
  error,
  submitting,
  action,
  masterProducts,
  alreadyOptedOutProductIds,
  onPartyChange,
  productsLoading,
  hasNoMasterProducts,
  masterProductsError,
  staleVersionError,
  onRefreshHandler,
  defaultCoverageOptOut,
}) => {
  const [id, setId] = useState(null);
  // Not user-editable: it is the row version this form was loaded with, held
  // only so submit can hand it back for the server's optimistic-lock check.
  const [version, setVersion] = useState(null);
  // Pre-selected, matching the Masters screen's Payer Side (v2) — with three
  // sides there is no longer a meaningful "none chosen" state to leave the
  // operator in, so Buyer starts checked the same way Masters defaults its
  // own radio group.
  const [partySide, setPartySide] = useState(PARTY_SIDE.BUYER);
  const [buyerNumber, setBuyerNumber] = useState('');
  const [sellerNumber, setSellerNumber] = useState('');
  // Buyer-branch-only (v2): the auction access number / "100 mil" / buyer rep
  // number. A rep can only ever VETO buyer-master coverage — never grant it —
  // so this never appears on the seller side.
  const [auctionAccessNumber, setAuctionAccessNumber] = useState('');
  const [productId, setProductId] = useState('');
  // Create is multi-select: a suppression is per (party, product), so fully
  // excluding a party means one row per product. Edit still governs exactly one
  // row, and its product is immutable, so it keeps `productId` above.
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState({});
  const [partyError, setPartyError] = useState(null);

  useEffect(() => {
    if (!isEmpty(defaultCoverageOptOut)) {
      setId(defaultCoverageOptOut.id);
      setVersion(
        defaultCoverageOptOut.version != null
          ? defaultCoverageOptOut.version
          : null
      );
      // sellerNumber alone cannot say WHICH seller-side master it vetoes — both
      // MASTER_SELLER and MSTR_SELLER_PAID share that column — so look the
      // product up in the already-loaded master list to tell edit's radio apart.
      if (defaultCoverageOptOut.sellerNumber) {
        const matched = (masterProducts || []).find(
          product => String(product.productId) === String(defaultCoverageOptOut.productId)
        );
        setPartySide(
          matched && matched.masterType === 'MSTR_SELLER_PAID'
            ? PARTY_SIDE.SELLER_PAID
            : PARTY_SIDE.SELLER
        );
      } else {
        setPartySide(PARTY_SIDE.BUYER);
      }
      setBuyerNumber(defaultCoverageOptOut.buyerNumber || '');
      setSellerNumber(defaultCoverageOptOut.sellerNumber || '');
      setAuctionAccessNumber(defaultCoverageOptOut.auctionAccessNumber || '');
      setProductId(defaultCoverageOptOut.productId || '');
      setStartDate(
        defaultCoverageOptOut.startDate ? moment(defaultCoverageOptOut.startDate) : null
      );
      setEndDate(
        defaultCoverageOptOut.endDate ? moment(defaultCoverageOptOut.endDate) : null
      );
      setNotes(defaultCoverageOptOut.notes || '');
    }
  }, [defaultCoverageOptOut, masterProducts]);

  const isEdit = action === 'edit';

  // Asks the container which products this party is ALREADY opted out of. Driven by
  // an effect rather than from inside the change handlers, because the side and the
  // identifiers together identify the party — this way a future edit to one handler
  // cannot forget the others. The container decides what is worth querying.
  useEffect(() => {
    if (!onPartyChange) return;
    onPartyChange(partySide, { buyerNumber, sellerNumber, auctionAccessNumber });
  }, [partySide, buyerNumber, sellerNumber, auctionAccessNumber]);

  // Only the masters for the CHOSEN side. The picker used to offer every seeded
  // master regardless of side, so Buyer + a MASTER_SELLER product was savable —
  // and permanently inert, because the buyer branch only ever resolves
  // MASTER_BUYER. Switching the radio re-filters rather than leaving a stale list.
  const sideProducts = productsForPartySide(masterProducts, partySide);
  const alreadySelected = new Set((alreadyOptedOutProductIds || []).map(String));

  // Unlike the Masters screen, this form already has every seeded master
  // product loaded up front (masterProducts, unfiltered by side) before any
  // radio is even touched — so which sides are actually usable is known in
  // advance, and each radio can say so directly instead of a banner appearing
  // only after the operator picks a dead end. Left enabled while the query is
  // still in flight or failed: disabling on an unknown answer would be a guess,
  // and both those states already block Create by other means.
  const sideIsUsable = side => (
    isEdit
    || productsLoading
    || masterProductsError
    || productsForPartySide(masterProducts, side).length > 0
  );

  // Masters exist somewhere, but none on the side the operator picked. Kept apart
  // from the global "nothing is seeded" state because the fix differs: switch
  // sides, or add a master for this side. Without it the picker renders empty and
  // Create fails with "select at least one product" - advice the operator cannot act on.
  const noProductsOnThisSide = !isEdit
    && !productsLoading
    && !hasNoMasterProducts
    && !masterProductsError
    // Only once a side is actually chosen. Before that the list is empty by
    // definition, and claiming "no coverage on this party side" would be
    // nonsense - there is no side yet. It would also switch Create off and
    // swallow the "choose Buyer or Seller" error the operator needs.
    && Boolean(partySide)
    && sideProducts.length === 0;
  // Nothing seeded to opt out of. Only ever true on create, and only once the
  // query has come back - while it is in flight the picker loads instead.
  const noMasterProducts = isCreateBlockedWithoutMasters(
    isEdit,
    hasNoMasterProducts,
    masterProductsError
  );
  // Same two checks submitForm runs before sending the request, hoisted here so
  // Create is not clickable at all until they are satisfied - previously they
  // only fired an inline error AFTER a click that visibly did nothing. Presence
  // only (not format): a malformed number is still "present" and stays a
  // submit-time error, matching every other field-shaped validation on this form.
  const noProductsSelected = !isEdit && selectedProductIds.length === 0;
  const missingPartyIdentifier = !isEdit && Boolean(
    partySelectionError(partySide, buyerNumber, sellerNumber, auctionAccessNumber)
  );
  // Only ever one of the two banners: the failed load is the more specific
  // claim, so it wins and the "nothing is seeded" line is suppressed - saying
  // both would be contradictory.
  const showMasterProductsError = !isEdit && Boolean(masterProductsError);
  const showNoMasterProducts = !isEdit
    && Boolean(hasNoMasterProducts)
    && !showMasterProductsError;
  const readOnlyClassName = isEdit ? 'input-disabled' : '';

  // The product cannot change on update, so edit shows it as text: a dropdown
  // there would invite an edit that cannot be made. It also has to stay text
  // because the picker now only lists products that have a master - a legacy
  // row pointing at a product with none would render a blank/invalid dropdown
  // and be impossible to retire.
  const productLabel = defaultCoverageOptOut
    ? String(
      defaultCoverageOptOut.productCode || defaultCoverageOptOut.productId || ''
    )
    : '';

  const onChangeHandler = (setter, field) => event => {
    setter(event.target.value);
    setErrors(prev => ({ ...prev, [field]: undefined }));
    setPartyError(null);
  };

  const onPartySideChangeHandler = side => () => {
    setPartySide(side);
    // The product list re-filters to the new side, so anything ticked for the old
    // side is no longer offerable and must not be carried over and submitted.
    setSelectedProductIds([]);
    setErrors(prev => ({ ...prev, partySide: undefined, productId: undefined }));
    setPartyError(null);
  };

  const toggleProduct = toggledId => {
    const key = String(toggledId);
    setSelectedProductIds(previous => (previous.map(String).includes(key)
      ? previous.filter(existing => String(existing) !== key)
      : [...previous, toggledId]));
    setErrors(prev => ({ ...prev, productId: undefined }));
  };

  // "Don't charge this dealer at all" is the intent operators actually have, and
  // opting out of every product on the side is the way to express it. One click
  // rather than N. Already-opted-out products are skipped — re-creating them
  // would be refused as duplicates.
  const selectAllProducts = () => {
    setSelectedProductIds(
      sideProducts
        .filter(product => !alreadySelected.has(String(product.productId)))
        .map(product => product.productId)
    );
    setErrors(prev => ({ ...prev, productId: undefined }));
  };

  const onStartDateChangeHandler = newDate => {
    setStartDate(newDate ? moment(newDate) : null);
  };

  const onEndDateChangeHandler = newDate => {
    setEndDate(newDate ? moment(newDate) : null);
    setErrors(prev => ({ ...prev, endDate: undefined }));
  };

  const submitForm = () => {
    // Guarded here as well as on the button: with no seeded master product
    // there is nothing this opt-out could suppress, so it must not be sent.
    if (noMasterProducts) return;
    // Same reasoning, per side: nothing on this side could be suppressed.
    if (noProductsOnThisSide) return;

    const trimmedBuyerNumber = buyerNumber.trim();
    const trimmedSellerNumber = sellerNumber.trim();
    const trimmedAan = auctionAccessNumber.trim();

    // On create the product is chosen by checkbox, so `required` cannot police it
    // — the array is validated explicitly below. Edit still has a single product,
    // and it is immutable, so it is always present.
    const formState = {
      partySide,
      productId: isEdit ? productId : 'multi',
    };
    const validation = new Validator(formState, validationRules);
    const validationFailed = validation.fails();
    const selectionError = partySelectionError(
      partySide, trimmedBuyerNumber, trimmedSellerNumber, trimmedAan
    );
    const buyerFormatError = partyNumberFormatError(trimmedBuyerNumber);
    const sellerFormatError = partyNumberFormatError(trimmedSellerNumber);
    const aanError = aanFormatError(trimmedAan);
    const notesError = notesLengthError(notes);
    const dateOrderError = startDate && endDate && endDate.isBefore(startDate)
      ? END_BEFORE_START_ERROR
      : null;

    // Format errors join the same per-field map validatorjs produces (and in its
    // shape - an array per field), so they render through the existing <span>
    // under each field rather than needing a second error surface.
    const fieldErrors = validationFailed ? validation.errors.all() : {};
    if (buyerFormatError) fieldErrors.buyerNumber = [buyerFormatError];
    if (sellerFormatError) fieldErrors.sellerNumber = [sellerFormatError];
    if (aanError) fieldErrors.auctionAccessNumber = [aanError];
    if (notesError) fieldErrors.notes = [notesError];
    if (dateOrderError) fieldErrors.endDate = [dateOrderError];
    if (noProductsSelected) fieldErrors.productId = [NO_PRODUCTS_SELECTED_ERROR];

    setErrors(fieldErrors);
    setPartyError(selectionError);
    if (validationFailed || selectionError || buyerFormatError || sellerFormatError
        || aanError || notesError || dateOrderError || noProductsSelected) return;

    onSubmitHandler({
      id,
      version,
      partySide,
      buyerNumber: partySide === PARTY_SIDE.BUYER ? (trimmedBuyerNumber || null) : null,
      sellerNumber: partySide !== PARTY_SIDE.BUYER ? (trimmedSellerNumber || null) : null,
      auctionAccessNumber: partySide === PARTY_SIDE.BUYER ? (trimmedAan || null) : null,
      productId,
      // Create submits N rows in one action; edit governs the one row it loaded.
      productIds: isEdit ? [productId] : selectedProductIds,
      startDate: startDate ? formatDateOnly(startDate) : null,
      endDate: endDate ? formatDateOnly(endDate) : null,
      notes: notes.trim() || null,
    });
  };

  return (
    <Modal
      data-testid='default-coverage-opt-out-form-modal'
      open={isModalOpen}
      closeIcon
      onClose={onCloseHandler}
      closeOnEscape={false}
      closeOnRootNodeClick={false}
    >
      <Modal.Header data-testid='default-coverage-opt-out-form-title'>
        {!id && 'Create New Default Coverage Opt-Out'}
        {id && 'Edit Default Coverage Opt-Out'}
      </Modal.Header>
      <Modal.Content image>
        <Modal.Description>
          {error && (
            <Message negative>
              <p>{error}</p>
            </Message>
          )}
          {/* A lost optimistic-lock race is not a generic failure: the edit did
              not apply and re-submitting the same loaded version never will, so
              it gets its own headline and the Update button is swapped for
              Refresh below. */}
          {staleVersionError && (
            <Message
              negative
              icon
              data-testid='default-coverage-opt-out-stale-version-error'
            >
              <Icon name='warning sign' />
              <Message.Content>
                <Message.Header>{STALE_VERSION_HEADER}</Message.Header>
                <p>{staleVersionError}</p>
                <p>{STALE_VERSION_GUIDANCE}</p>
              </Message.Content>
            </Message>
          )}
          <Message info data-testid='default-coverage-opt-out-form-help'>
            <p>
              An opt-out stops product-level default (master) coverage from
              being applied to one party on one product. It applies to future
              sales only and does not cancel coverage that already exists. It
              does not override an active individual subscription, and it is
              scoped to this party and product only - other products and the
              other party side are unaffected.
            </p>
            {/* The picker is fed by the seeded master products, not the
                catalog, so name that and say what a row against an unseeded
                product would (not) do. */}
            <p>{MASTER_PRODUCTS_ONLY_GUIDANCE}</p>
            {/* The picker reads as a product list, not as a coverage switch, so the
                consequence of ticking all of it is stated before the operator does
                it — the contextual warning below only appears once they have. */}
            <p data-testid='default-coverage-opt-out-select-all-guidance'>
              {SELECT_ALL_GUIDANCE}
            </p>
          </Message>
          {/* No product has default coverage seeded, so there is no dropdown at
              all: an opt-out created here would be inert. Create is switched
              off below and submitForm refuses too. */}
          {showNoMasterProducts && (
            <Message
              warning
              icon
              data-testid='default-coverage-opt-out-no-master-products'
            >
              <Icon name='warning sign' />
              <Message.Content>
                <Message.Header>{NO_MASTER_PRODUCTS_HEADER}</Message.Header>
                <p>{NO_MASTER_PRODUCTS_GUIDANCE}</p>
              </Message.Content>
            </Message>
          )}
          {/* The products query failed. Negative, not warning: this is a broken
              load, not a legitimate empty state, and it must not read as
              "nothing is seeded" - that sends Ops off to seed a product they may
              already have. Create is switched off below and submitForm refuses
              here too. */}
          {showMasterProductsError && (
            <Message
              negative
              icon
              data-testid='default-coverage-opt-out-master-products-error'
            >
              <Icon name='exclamation triangle' />
              <Message.Content>
                <Message.Header>{MASTER_PRODUCTS_ERROR_HEADER}</Message.Header>
                <p>{MASTER_PRODUCTS_ERROR_GUIDANCE}</p>
              </Message.Content>
            </Message>
          )}
          {/* Masters exist, just not on this side - a different problem from
              "nothing is seeded", so it gets its own wording and its own fix.
              Negative (red): there is nothing this screen can do about it. */}
          {noProductsOnThisSide && (
            <Message negative icon data-testid='default-coverage-opt-out-no-products-this-side'>
              <Icon name='exclamation triangle' />
              <Message.Content>
                <Message.Header>{NO_PRODUCTS_ON_SIDE_HEADER}</Message.Header>
                <p>{NO_PRODUCTS_ON_SIDE_GUIDANCE}</p>
              </Message.Content>
            </Message>
          )}
          {partyError && (
            <Message negative data-testid='default-coverage-opt-out-party-error'>
              <p>{partyError}</p>
            </Message>
          )}
          <Form>
            <Header size='small' color='blue' />
            <Form.Group widths='equal'>
              <Form.Field required error={!isEmpty(errors.partySide)}>
                <label htmlFor='partySideBuyer'>Party Side</label>
                <Radio
                  id='partySideBuyer'
                  data-testid='default-coverage-opt-out-party-side-buyer'
                  label='Buyer'
                  name='partySide'
                  value={PARTY_SIDE.BUYER}
                  checked={partySide === PARTY_SIDE.BUYER}
                  onChange={onPartySideChangeHandler(PARTY_SIDE.BUYER)}
                  disabled={isEdit || !sideIsUsable(PARTY_SIDE.BUYER)}
                />
                <Radio
                  id='partySideSeller'
                  data-testid='default-coverage-opt-out-party-side-seller'
                  label='Seller'
                  name='partySide'
                  value={PARTY_SIDE.SELLER}
                  checked={partySide === PARTY_SIDE.SELLER}
                  onChange={onPartySideChangeHandler(PARTY_SIDE.SELLER)}
                  disabled={isEdit || !sideIsUsable(PARTY_SIDE.SELLER)}
                  style={{ marginLeft: '1em' }}
                />
                <Radio
                  id='partySideSellerPaid'
                  data-testid='default-coverage-opt-out-party-side-seller-paid'
                  label='Seller-paid'
                  name='partySide'
                  value={PARTY_SIDE.SELLER_PAID}
                  checked={partySide === PARTY_SIDE.SELLER_PAID}
                  onChange={onPartySideChangeHandler(PARTY_SIDE.SELLER_PAID)}
                  disabled={isEdit || !sideIsUsable(PARTY_SIDE.SELLER_PAID)}
                  style={{ marginLeft: '1em' }}
                />
                {errors.partySide && <span>{errors.partySide}</span>}
              </Form.Field>
              {partySide !== PARTY_SIDE.BUYER && (
                <Form.Field required error={!isEmpty(errors.sellerNumber)}>
                  <label htmlFor='sellerNumber'>Seller Number</label>
                  <input
                    id='sellerNumber'
                    name='sellerNumber'
                    data-testid='default-coverage-opt-out-party-number-input'
                    value={sellerNumber}
                    onChange={onChangeHandler(setSellerNumber, 'sellerNumber')}
                    readOnly={isEdit}
                    className={readOnlyClassName}
                    maxLength={PARTY_NUMBER_LENGTH}
                    inputMode='numeric'
                  />
                  {errors.sellerNumber && <span>{errors.sellerNumber}</span>}
                </Form.Field>
              )}
            </Form.Group>
            {/* Buyer-branch-only (v2): a rep can only ever VETO buyer-master
                coverage, and can do so by number alone, AAN alone, or both — so
                the two identifiers share a row and neither shows on the seller
                or seller-paid side. */}
            {partySide === PARTY_SIDE.BUYER && (
              <Form.Group widths='equal'>
                <Form.Field error={!isEmpty(errors.buyerNumber)}>
                  <label htmlFor='buyerNumber'>Buyer Number</label>
                  <input
                    id='buyerNumber'
                    name='buyerNumber'
                    data-testid='default-coverage-opt-out-party-number-input'
                    value={buyerNumber}
                    onChange={onChangeHandler(setBuyerNumber, 'buyerNumber')}
                    readOnly={isEdit}
                    className={readOnlyClassName}
                    maxLength={PARTY_NUMBER_LENGTH}
                    inputMode='numeric'
                  />
                  {errors.buyerNumber && <span>{errors.buyerNumber}</span>}
                </Form.Field>
                <Form.Field error={!isEmpty(errors.auctionAccessNumber)}>
                  <label htmlFor='auctionAccessNumber'>Auction Access Number</label>
                  <input
                    id='auctionAccessNumber'
                    name='auctionAccessNumber'
                    data-testid='default-coverage-opt-out-aan-input'
                    value={auctionAccessNumber}
                    onChange={onChangeHandler(setAuctionAccessNumber, 'auctionAccessNumber')}
                    readOnly={isEdit}
                    className={readOnlyClassName}
                    maxLength={AAN_LENGTH}
                    inputMode='numeric'
                  />
                  {errors.auctionAccessNumber && <span>{errors.auctionAccessNumber}</span>}
                </Form.Field>
              </Form.Group>
            )}
            <Form.Group widths='equal'>
              {isEdit && (
                <Form.Field>
                  <label htmlFor='productCode'>Product</label>
                  <input
                    id='productCode'
                    name='productCode'
                    data-testid='default-coverage-opt-out-product-readonly'
                    value={productLabel}
                    readOnly
                    className={readOnlyClassName}
                  />
                </Form.Field>
              )}
              {!isEdit && !noMasterProducts && (
                <Form.Field required error={!isEmpty(errors.productId)}>
                  <label htmlFor='productIds'>Products to opt out of</label>
                  {productsLoading && (
                    <Select
                      id='productIds'
                      data-testid='default-coverage-opt-out-products-loading'
                      fluid
                      placeholder='Loading products...'
                      options={[]}
                      loading
                      disabled
                    />
                  )}
                  {!productsLoading && (
                    <Table
                      compact='very'
                      size='small'
                      data-testid='default-coverage-opt-out-product-list'
                    >
                      <Table.Body>
                        {sideProducts.map(product => {
                          const key = String(product.productId);
                          const already = alreadySelected.has(key);
                          return (
                            <Table.Row key={key}>
                              <Table.Cell collapsing>
                                <Checkbox
                                  data-testid={`default-coverage-opt-out-product-${key}`}
                                  checked={already || selectedProductIds.map(String).includes(key)}
                                  /* Already-opted-out products render checked and
                                     locked: the row exists, so re-creating it would
                                     be refused as a duplicate. Showing it checked
                                     makes the form double as "what is this party
                                     opted out of?". */
                                  disabled={already}
                                  onChange={() => toggleProduct(product.productId)}
                                />
                              </Table.Cell>
                              <Table.Cell>
                                {product.productName
                                  ? `${product.productCode} - ${product.productName}`
                                  : product.productCode}
                                {already && (
                                  <span
                                    style={{ marginLeft: '0.5em', fontStyle: 'italic' }}
                                    data-testid={`default-coverage-opt-out-already-${key}`}
                                  >
                                    · already opted out
                                  </span>
                                )}
                              </Table.Cell>
                            </Table.Row>
                          );
                        })}
                      </Table.Body>
                    </Table>
                  )}
                  {!productsLoading && sideProducts.length > 0 && (
                    <div style={{ marginTop: '0.5em' }}>
                      <Button
                        size='mini'
                        type='button'
                        data-testid='default-coverage-opt-out-select-all'
                        onClick={selectAllProducts}
                      >
                        Select all
                      </Button>
                      <Button
                        size='mini'
                        type='button'
                        data-testid='default-coverage-opt-out-clear-all'
                        onClick={() => setSelectedProductIds([])}
                      >
                        Clear
                      </Button>
                    </div>
                  )}
                  {errors.productId && <span>{errors.productId}</span>}
                </Form.Field>
              )}
              <Form.Field>
                <label htmlFor='startDate'>Start Date</label>
                <DatePicker
                  id='startDate'
                  customInput={(
                    <input
                      data-testid='default-coverage-opt-out-start-input'
                      style={{ padding: '0', width: '100%' }}
                    />
                  )}
                  selected={startDate}
                  onChange={onStartDateChangeHandler}
                  dateFormat='YYYY-MM-DD'
                  placeholderText='Leave blank to start now'
                  isClearable
                />
              </Form.Field>
              <Form.Field error={!isEmpty(errors.endDate)}>
                <label htmlFor='endDate'>End Date</label>
                <DatePicker
                  id='endDate'
                  customInput={(
                    <input
                      data-testid='default-coverage-opt-out-end-input'
                      style={{ padding: '0', width: '100%' }}
                    />
                  )}
                  selected={endDate}
                  onChange={onEndDateChangeHandler}
                  dateFormat='YYYY-MM-DD'
                  placeholderText='Leave blank to never expire'
                  isClearable
                />
                {errors.endDate && <span>{errors.endDate}</span>}
              </Form.Field>
            </Form.Group>
            <Form.Group widths='equal'>
              <Form.Field error={!isEmpty(errors.notes)}>
                <label htmlFor='notes'>Notes</label>
                <input
                  id='notes'
                  name='notes'
                  data-testid='default-coverage-opt-out-notes-input'
                  value={notes}
                  onChange={onChangeHandler(setNotes, 'notes')}
                  maxLength={NOTES_MAX_LENGTH}
                />
                {errors.notes && <span>{errors.notes}</span>}
              </Form.Field>
            </Form.Group>
          </Form>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        {submitting && (
          <Button loading positive>
            Submitting...
          </Button>
        )}
        {!submitting && !staleVersionError && !id && (
          <Button
            data-testid='default-coverage-opt-out-form-submit'
            color='green'
            onClick={submitForm}
            disabled={
              noMasterProducts || noProductsOnThisSide
              || noProductsSelected || missingPartyIdentifier
            }
            inverted
          >
            <Icon name='checkmark' /> Create
          </Button>
        )}
        {!submitting && !staleVersionError && id && (
          <Button
            data-testid='default-coverage-opt-out-form-submit'
            color='green'
            onClick={submitForm}
            inverted
          >
            <Icon name='checkmark' /> Update
          </Button>
        )}
        {/* Nothing this form can send will succeed until the row is reloaded,
            so the only forward action offered is the refresh. */}
        {!submitting && staleVersionError && onRefreshHandler && (
          <Button
            data-testid='default-coverage-opt-out-form-refresh'
            color='blue'
            onClick={onRefreshHandler}
            inverted
          >
            <Icon name='refresh' /> Refresh
          </Button>
        )}
        <Button
          data-testid='default-coverage-opt-out-form-cancel'
          onClick={onCloseHandler}
        >
          Cancel
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

DefaultCoverageOptOutForm.propTypes = {
  isModalOpen: PropTypes.bool,
  onCloseHandler: PropTypes.func.isRequired,
  onSubmitHandler: PropTypes.func.isRequired,
  error: PropTypes.string,
  submitting: PropTypes.bool,
  action: PropTypes.string,
  // The RAW master products, not pre-mapped options: the picker needs masterType
  // to filter by party side, which does not survive a mapping to {key,value,text}.
  masterProducts: PropTypes.arrayOf(
    PropTypes.shape({
      productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      productCode: PropTypes.string,
      productName: PropTypes.string,
      masterType: PropTypes.string,
    })
  ),
  alreadyOptedOutProductIds: PropTypes.array,
  onPartyChange: PropTypes.func,
  productsLoading: PropTypes.bool,
  hasNoMasterProducts: PropTypes.bool,
  masterProductsError: PropTypes.bool,
  staleVersionError: PropTypes.string,
  onRefreshHandler: PropTypes.func,
  defaultCoverageOptOut: PropTypes.shape({
    id: PropTypes.string,
    version: PropTypes.number,
    buyerNumber: PropTypes.string,
    sellerNumber: PropTypes.string,
    auctionAccessNumber: PropTypes.string,
    productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    productCode: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    notes: PropTypes.string,
    createdBy: PropTypes.string,
    updatedBy: PropTypes.string,
    updatedAt: PropTypes.string,
  }),
};

DefaultCoverageOptOutForm.defaultProps = {
  isModalOpen: false,
  error: null,
  submitting: false,
  action: 'new',
  masterProducts: [],
  alreadyOptedOutProductIds: [],
  productsLoading: false,
  hasNoMasterProducts: false,
  masterProductsError: false,
  staleVersionError: null,
  onRefreshHandler: null,
  defaultCoverageOptOut: null,
};

export default DefaultCoverageOptOutForm;
